import React, { useCallback } from "react";
import glamorous from "glamorous";
import cx from "classnames";
import { connect } from "react-redux";
import Spinner from "@clds/progress";
import { WIDGET_VIEW_TYPES } from "@cld/upload-widget";
import t from "common/i18n";
import { bindStructuredActions } from "common/actionsBase";
import bindRootActions, { types as rootAts } from "root/actions";
import MenuButton from "root/components/MenuButton/MenuButton";
import PoweredBy from "root/components/PoweredBy/PoweredBy";
import Progress from "root/components/Progress/Progress";
import {
	selectShowPoweredBy,
	selectIsInlineMode,
} from "root/selectors";
import {
	selectUploadQueueItemIds,
	selectQueueCounters,
	selectQueueProgress,
} from "../../selectors";
import ExpandedItem from "../ExpandedItem/ExpandedItem";
import ExpandedQueueActionButtons from "../ExpandedQueueActionButtons";
import VirtualizedQueueItemsGrid from "../VirtualizedQueueItemsGrid";
import SVG_ICONS from "~/assets/icons/icons";

// type OwnProps = {|
// 	showCompleted: Function,
// |};
// type Props = {
// 	...OwnProps,
// 	queueIds: string[],
// 	queueProgress: number,
// 	active: number,
// 	failed: number,
// 	done: number,
// 	isSimulated: boolean,
// 	isDone: boolean,
// 	rootActions: RootActions,
// 	showPoweredBy: boolean,
// 	isInline: boolean,
// };

const GExpandedQueue = glamorous.div(cx(
	"ExpandedQueue",
	"relative left-0 right-0 bottom-0 top-0",
	"w-100 h-100",
	"flex flex-column",
),
{
	borderRadius: 6,
	boxShadow: "0 20px 30px rgba(0,0,0,.5)",
});

const GHeader = glamorous.div(cx(
	"ExpandedQueue--HEAD",
	"flex-none",
	"flex items-center justify-between",
	"bg-window bg-window-c",
	"bb bc-window-border",
), {
	height: 52,
	paddingLeft: 20,
	paddingRight: 20,
	boxShadow: "0 2px 4px 0 rgba(0,0,0,0.5)",
});

const GBody = glamorous.div(cx(
	"ExpandedQueue--BODY",
	"overflow-y-auto",
	"flex-grow flex-wrap",
	"flex content-start",
	"bg-source-bg bg-source-bg-c",
), {
	padding: "9px 0 9px 9px",
});

const GFooter = glamorous.div((props) => cx(
	"ExpandedQueue--FOOTER",
	"flex items-center",
	"bg-window bg-window-c",
	"bt bc-window-border",
	{
		"justify-end": !props.showPoweredBy,
		"justify-between": props.showPoweredBy,
	},
), {
	padding: "9px 16px",
});

const getQueueTitle = ({ isDone, isSimulated, hasActive, active, hasProcessing, processing }) => {
	if (isDone) {
		if (hasProcessing) {
			return t("queue.title_processing_with_counter", { num: processing });
		}
		return t("queue.title");
	}
	if (isSimulated) {
		return t("queue.title_uploading");
	}
	if(hasActive && hasProcessing) {
		return t("queue.title_uploading_processing_with_counters", { uploading: active, processing });
	}
	if(hasProcessing) {
		return t("queue.title_processing_with_counter", { num: processing });
	}
	return t("queue.title_uploading_with_counter", { num: active });
};

export const ExpandedQueue = (props) => {
	const title = getQueueTitle(props);
	const setQueueItemsRetryAction = props.rootActions[rootAts.SET_QUEUE_ITEMS_RETRY],
		setQueueItemsAbort = props.rootActions[rootAts.SET_QUEUE_ITEMS_ABORT];

	const retryFailedItem = useCallback((id) =>
			setQueueItemsRetryAction({ ids: [id] }),
		[setQueueItemsRetryAction]);

	const abortItem = useCallback((id) =>
			setQueueItemsAbort({ ids: [id] }),
		[setQueueItemsAbort]);

	return <GExpandedQueue data-test="expanded-upload-queue">
		<GHeader data-test="upload-queue-header">
			<glamorous.Div className="flex items-center fw5 f5" data-test="upload-queue-title">
				{!props.isDone && 
					<glamorous.Div className="mr3">
						{props.hasActive ? <Progress size={33} percentage={props.queueProgress} /> : <Spinner.Circular size="lg" variant="primary" />}
					</glamorous.Div>}
				{title}
			</glamorous.Div>
			{!props.isInline ?
				<glamorous.Div className="flex flex-nowrap items-center justify-end"
				               data-test="eq-close-mini-box">
					<MenuButton dataTest="minimize-dialog-icon"
					            iconSize="sm"
					            className="mr6"
					            icon={SVG_ICONS.KEYBOARD_DOWN}
					            onClick={() => props.rootActions[rootAts.SET_WIDGET_VIEW_TYPE]({ type: WIDGET_VIEW_TYPES.MINI })}
					            tabIndex={0}
					/>
				</glamorous.Div> : null}
		</GHeader>
		<GBody>
			{props.queueIds.length === 1 ?
				<ExpandedItem
					className="w-100"
					id={props.queueIds[0]}
					paddingBottom="430px"
					gutter={0}
					retryFailed={retryFailedItem}
					abort={abortItem}
				/> :
				<VirtualizedQueueItemsGrid
					retryFailed={retryFailedItem}
					abort={abortItem}
					queueIds={props.queueIds}
				/>}
		</GBody>
		<GFooter showPoweredBy={props.showPoweredBy}>
			{props.showPoweredBy ? <PoweredBy/> : null}
			<ExpandedQueueActionButtons
				activeCount={props.active}
				doneCount={props.done}
				failedCount={props.failed}
				onShowCompleted={props.showCompleted}
				isDone={props.isDone}
				isInline={props.isInline}
			/>
		</GFooter>
	</GExpandedQueue>;
};

export default connect(
	(state) => ({
		...selectQueueCounters(state),
		queueIds: selectUploadQueueItemIds(state),
		showPoweredBy: selectShowPoweredBy(state),
		queueProgress: selectQueueProgress(state),
		isInline: selectIsInlineMode(state),
	}),
	bindStructuredActions({
		rootActions: bindRootActions,
	}),
)(ExpandedQueue);
